import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatErr, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Upload, Map as MapIcon, RotateCcw } from "lucide-react";

function ReviewRow({ z, market, onSaved, onUpload }) {
  const [text, setText] = useState(z.boundary_description || "");
  const confirmed = z.boundary_status === "Confirmed";
  const dirty = text !== (z.boundary_description || "");
  const save = async (status) => {
    try { await api.put(`/mz/zones/${z.id}`, { boundary_description: text, boundary_status: status }); toast.success(status === "Confirmed" ? `${z.name} confirmed` : "Saved as draft"); onSaved(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="grid gap-3 md:grid-cols-[200px_1fr_220px] items-start py-3 border-t border-slate-100" data-testid={`br-zone-${z.id}`}>
      <div><p className="text-[11px] uppercase tracking-wide text-slate-400">{market}</p><p className="font-semibold text-slate-800">{z.name}</p>
        <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${confirmed ? "bg-[#9ACC32]/15 text-[#4A700A]" : "bg-amber-100 text-amber-800"}`} data-testid={`br-status-${z.id}`}>{z.boundary_status || "Draft — confirm"}</span></div>
      <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid={`br-text-${z.id}`} />
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-1.5">
          {!confirmed || dirty ? <Button size="sm" className="h-7 text-xs flex-1" onClick={() => save("Confirmed")} data-testid={`br-confirm-${z.id}`}><CheckCircle2 className="h-3 w-3" />Confirm</Button>
            : <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => save("Draft — confirm")} data-testid={`br-reopen-${z.id}`}><RotateCcw className="h-3 w-3" />Back to draft</Button>}
          {dirty && !confirmed && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => save("Draft — confirm")} data-testid={`br-save-${z.id}`}>Save draft</Button>}
        </div>
        <div className="flex gap-1.5">
          {z.map ? <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => downloadBlob(`/library/${z.map.id}/download`, z.map.original_filename)} data-testid={`br-map-view-${z.id}`}><MapIcon className="h-3 w-3" />View map</Button>
            : <span className="flex-1 rounded bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-600 text-center" data-testid={`br-map-needed-${z.id}`}>Map needed</span>}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onUpload(z)} data-testid={`br-map-upload-${z.id}`}><Upload className="h-3 w-3" />{z.map ? "Replace" : "Upload"}</Button>
        </div>
      </div>
    </div>
  );
}

export default function BoundaryReview() {
  const [markets, setMarkets] = useState([]);
  const [only, setOnly] = useState("todo");
  const fileRef = useRef();
  const target = useRef(null);
  const load = useCallback(() => api.get("/mz/tree").then((r) => setMarkets(r.data.markets)), []);
  useEffect(() => { load(); }, [load]);
  const zones = markets.filter((m) => m.status !== "inactive").flatMap((m) => m.zones.filter((z) => z.status !== "inactive").map((z) => ({ z, market: m.name })));
  const done = zones.filter(({ z }) => z.boundary_status === "Confirmed" && z.map).length;
  const shown = zones.filter(({ z }) => only === "all" || z.boundary_status !== "Confirmed" || !z.map);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/mz/zones/${target.current.id}/map`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Map saved for ${target.current.name}`); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { fileRef.current.value = ""; }
  };
  return (
    <div className="cc-card p-4" data-testid="boundary-review">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="font-display font-semibold text-slate-800">Boundary Review</h3>
          <p className="text-xs text-slate-400" data-testid="br-progress">{done} of {zones.length} zones confirmed with a map. Edit the wording if needed, press Confirm, and upload each final map (PDF or image).</p></div>
        <div className="flex gap-1.5">
          <Button size="sm" variant={only === "todo" ? "default" : "outline"} onClick={() => setOnly("todo")} data-testid="br-filter-todo">Needs attention</Button>
          <Button size="sm" variant={only === "all" ? "default" : "outline"} onClick={() => setOnly("all")} data-testid="br-filter-all">All zones</Button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={upload} data-testid="br-map-input" />
      <div className="mt-2">
        {shown.map(({ z, market }) => <ReviewRow key={`${z.id}-${z.updated_at}`} z={z} market={market} onSaved={load} onUpload={(zz) => { target.current = zz; fileRef.current?.click(); }} />)}
        {shown.length === 0 && <p className="text-sm text-slate-400 py-8 text-center" data-testid="br-all-done">Every active and future zone is confirmed and has a map.</p>}
      </div>
    </div>
  );
}
